import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BeanCard = ({ bean, language }) => {
  const [isHovered, setIsHovered] = useState(false);

  const certificationIcons = {
    organic: 'Leaf',
    fair_trade: 'Heart',
    fairtrade: 'Heart',
    rainforest_alliance: 'TreePine',
    rainforest: 'TreePine',
    utz: 'Award',
    bird_friendly: 'Bird',
    direct_trade: 'Handshake'
  };

  const processingTypeLabels = {
    en: {
      washed: 'Washed',
      natural: 'Natural',
      honey: 'Honey',
      semi_washed: 'Semi-Washed',
      'semi-washed': 'Semi-Washed',
      wet_hulled: 'Wet Hulled'
    },
    zh: {
      washed: '水洗',
      natural: '日晒',
      honey: '蜜处理',
      semi_washed: '半水洗',
      'semi-washed': '半水洗',
      wet_hulled: '湿刨法'
    }
  };

  // Get primary image or first image from bean_images array
  const getImageUrl = () => {
    if (bean?.images && Array.isArray(bean?.images) && bean?.images?.length > 0) {
      return bean?.images?.[0];
    }
    return 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=800';
  };

  const getImageAlt = () => {
    const beanName = language === 'en' ? bean?.nameEn : bean?.nameZh;
    return `${beanName} coffee beans from ${bean?.country?.nameEn || 'Africa'}`;
  };

  return (
    <div
      className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={getImageUrl()}
          alt={getImageAlt()}
          className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
          onError={(e) => {
            e.target.src = 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=800';
          }}
        />

        {/* Hover Overlay */}
        <div className={`absolute inset-0 bg-black/50 flex items-center justify-center transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <Link to={`/bean-detail?id=${bean?.id}`}>
            <Button variant="default" iconName="Eye" iconPosition="left">
              {language === 'en' ? 'View Details' : '查看详情'}
            </Button>
          </Link>
        </div>

        {/* Certifications Badge */}
        {bean?.certifications && bean?.certifications?.length > 0 && (
          <div className="absolute top-2 right-2 flex gap-1">
            {bean?.certifications?.slice(0, 3)?.map((cert) => (
              <div
                key={cert}
                className="bg-white/90 backdrop-blur-sm p-1.5 rounded-full"
                title={cert?.replace('_', ' ')?.toUpperCase()}
              >
                <Icon name={certificationIcons?.[cert] || 'Award'} size={16} color="#10b981" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-4">
        {/* Bean Name */}
        <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
          {language === 'en' ? bean?.nameEn : bean?.nameZh}
        </h3>

        {/* Country & Farm */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Icon name="MapPin" size={14} />
          <span>{language === 'en' ? bean?.country?.nameEn : bean?.country?.nameZh}</span>
          {bean?.farm?.nameEn && (
            <>
              <span>•</span>
              <span className="line-clamp-1">{language === 'en' ? bean?.farm?.nameEn : bean?.farm?.nameZh}</span>
            </>
          )}
        </div>

        {/* Specifications Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
          <div className="flex items-center gap-1">
            <Icon name="DollarSign" size={14} color="var(--color-primary)" />
            <span className="font-medium">${bean?.priceUsdPerKg?.toFixed(2)}/kg</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Mountain" size={14} color="var(--color-primary)" />
            <span>{bean?.altitudeM}m</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Droplets" size={14} color="var(--color-primary)" />
            <span>{processingTypeLabels?.[language]?.[bean?.processingType] || bean?.processingType}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="Award" size={14} color="var(--color-primary)" />
            <span>{bean?.grade || 'N/A'}</span>
          </div>
        </div>

        {/* Production Year & Availability */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border">
          <span>{language === 'en' ? 'Year' : '年份'}: {bean?.productionYear}</span>
          <span className="flex items-center gap-1">
            <Icon name="Package" size={12} />
            {bean?.availableQtyKg?.toLocaleString()} kg
          </span>
        </div>
      </div>
    </div>
  );
};

export default BeanCard;